/**
 * The "main image" of a content or a page, defined once.
 *
 * Lists, editor headers, the calendar and the version history all show a small
 * picture of what is being edited. They must agree on which picture, so the
 * rule lives here and nowhere else. The interface keeps a byte-identical copy
 * (apps/ui/src/domain/main-image.ts, because it depends on contracts only); a
 * test in the interface runs both over the same cases so they cannot drift.
 *
 * A content type can name its own image in `seo.image` ("cover", "hero.image"):
 * that wins. Otherwise the fields real sites use, in the order that matters:
 * the four seed types carry `cover` (article), `image` (caseStudy, technology)
 * and `hero.image` (course); pages migrated from v4 carry `cover` as an object.
 * Pure: no I/O, no dates, no randomness.
 */

export interface MainImage {
  /** The address as stored: `/media/2026/x.webp`, `/blog/x/img/cover.webp`, or a full URL. */
  path: string
  /** The alternative text written for it, when the document carries one. */
  alt: string | null
  /** Where it was found, for the one who wants to explain it: a field name or `section:hero`. */
  source: string
}

type Bag = Record<string, unknown>

/**
 * An explicit `mainImage` wins; then the fields of the seed types; then the
 * places the v4 import writes (`landing`, `card`). The imported contents have
 * neither `mainImage` nor `cover`: their picture lives in `extra.assets`,
 * found after the gallery.
 */
export const MAIN_IMAGE_FIELDS = [
  'mainImage', 'contents.mainImage',
  'cover', 'coverImage', 'image', 'heroImage', 'hero.image', 'ogImage', 'meta.ogImage', 'copertina', 'immagine',
  'landing.mainImage', 'landing.hero.image', 'landing.hero.cover', 'landing.image', 'landing.cover',
  'card.mainImage', 'card.image', 'card.immagine', 'card.cover',
] as const
/** Last resort: any picture-like key under these objects, to a bounded depth. */
const SCAN_ROOTS = ['landing', 'card', 'hero', 'extra'] as const
const IMAGE_KEY = /^(?:image|img|cover|photo|picture|thumbnail|thumb|immagine|copertina|hero|background(?:Image)?)$/i
const SCAN_DEPTH = 4
export const SECTION_IMAGE_PROPS = ['image', 'backgroundImage', 'poster', 'cover', 'photo'] as const
const IMAGE_EXTENSION = /\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i
const ADDRESS_KEYS = ['src', 'url', 'path'] as const

function isBag(value: unknown): value is Bag {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

/** An address that points at a picture: a library file or anything ending like one. */
export function looksLikeImagePath(value: string): boolean {
  if (/^\/?media\//.test(value)) return !/\.(?:pdf|mp4)(?:[?#].*)?$/i.test(value)
  if (/^data:/i.test(value)) return false
  return IMAGE_EXTENSION.test(value)
}

function readAt(source: Bag, dotted: string): unknown {
  let current: unknown = source
  for (const part of dotted.split('.')) {
    if (!isBag(current)) return undefined
    current = current[part]
  }
  return current
}

/** A field value as a picture: a bare address, or `{ src | url | path, alt }`. */
function pictureFrom(value: unknown, altSibling: string | null): { path: string; alt: string | null } | null {
  const direct = text(value)
  if (direct) return looksLikeImagePath(direct) ? { path: direct, alt: altSibling } : null
  if (!isBag(value)) return null
  for (const key of ADDRESS_KEYS) {
    const address = text(value[key])
    if (address && looksLikeImagePath(address)) return { path: address, alt: text(value.alt) ?? altSibling }
  }
  return null
}

/** The address inside a field value (a bare string or `{ src, alt }`), or null when it is not a picture. */
export function imageOfValue(value: unknown): { path: string; alt: string | null } | null {
  return pictureFrom(value, null)
}

/** `cover` → `coverAlt`, `hero.image` → `hero.imageAlt`: the sibling field the editor offers. */
function altSiblingOf(data: Bag, field: string): string | null {
  return text(readAt(data, `${field}Alt`))
}

/**
 * The main image of a content, or `null`. `hint` is the content type's
 * `seo.image` path when the caller has it; the projection does not, and the
 * default order finds the same field on every seed type.
 */
export function mainImageOfContent(data: Bag | null | undefined, hint?: string | null): MainImage | null {
  if (!data) return null
  const candidates = hint ? [hint, ...MAIN_IMAGE_FIELDS.filter((field) => field !== hint)] : MAIN_IMAGE_FIELDS
  for (const field of candidates) {
    const found = pictureFrom(readAt(data, field), altSiblingOf(data, field))
    if (found) return { ...found, source: field }
  }
  const gallery = data.gallery
  if (Array.isArray(gallery)) {
    for (const item of gallery) {
      const found = pictureFrom(item, null)
      if (found) return { ...found, source: 'gallery' }
    }
  }
  const asset = firstImageAsset(readAt(data, 'extra.assets'))
  if (asset) return { ...asset, source: 'extra.assets' }
  for (const root of SCAN_ROOTS) {
    const found = scan(data[root], SCAN_DEPTH)
    if (found) return { ...found, source: `${root}.*` }
  }
  return null
}

/**
 * `extra.assets` of an imported content: `[{ type: 'image', url }]`. An entry
 * typed as image counts even when its address has no extension (a CDN link);
 * an untyped one must look like a picture.
 */
function firstImageAsset(value: unknown): { path: string; alt: string | null } | null {
  if (!Array.isArray(value)) return null
  for (const item of value) {
    if (!isBag(item)) {
      const bare = pictureFrom(item, null)
      if (bare) return bare
      continue
    }
    const kind = text(item.type)?.toLowerCase()
    if (kind && kind !== 'image') continue
    const found = pictureFrom(item, null)
    if (found) return found
    const address = ADDRESS_KEYS.map((key) => text(item[key])).find((entry) => entry !== null)
    if (kind === 'image' && address && !/^data:/i.test(address) && !/\.(?:pdf|mp4)(?:[?#].*)?$/i.test(address)) return { path: address, alt: text(item.alt) }
  }
  return null
}

function scan(value: unknown, depth: number): { path: string; alt: string | null } | null {
  if (depth < 0) return null
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = scan(item, depth - 1)
      if (found) return found
    }
    return null
  }
  if (!isBag(value)) return null
  for (const [key, inner] of Object.entries(value)) {
    if (IMAGE_KEY.test(key)) {
      const found = pictureFrom(inner, null)
      if (found) return found
    }
  }
  for (const [key, inner] of Object.entries(value)) {
    if (key === 'assets') {
      const found = firstImageAsset(inner)
      if (found) return found
    } else if (isBag(inner) || Array.isArray(inner)) {
      const found = scan(inner, depth - 1)
      if (found) return found
    }
  }
  return null
}

interface SectionLike {
  sectionKey?: unknown
  props?: unknown
  children?: unknown
}

function pictureOfSection(section: SectionLike): { path: string; alt: string | null } | null {
  if (!isBag(section.props)) return null
  const props = section.props
  for (const key of SECTION_IMAGE_PROPS) {
    const found = pictureFrom(props[key], null)
    if (found) return found
  }
  // Any other property that is itself a picture ({ src, width, height, alt }).
  for (const value of Object.values(props)) {
    if (isBag(value) && text(value.src)) {
      const found = pictureFrom(value, null)
      if (found) return found
    }
  }
  return null
}

/**
 * The main image of a page: the share image of its SEO if set, else the first
 * picture met walking the sections top to bottom, children right after their
 * parent (the order the page is read in).
 */
export function mainImageOfPage(page: { seo?: { ogImage?: unknown } | null; sections?: readonly unknown[] | null }): MainImage | null {
  const og = text(page.seo?.ogImage)
  if (og && looksLikeImagePath(og)) return { path: og, alt: null, source: 'seo.ogImage' }
  const walk = (sections: readonly unknown[] | null | undefined): MainImage | null => {
    for (const raw of sections ?? []) {
      if (!isBag(raw)) continue
      const section = raw as SectionLike
      const found = pictureOfSection(section)
      if (found) return { ...found, source: `section:${text(section.sectionKey) ?? 'unknown'}` }
      const nested = walk(Array.isArray(section.children) ? section.children : null)
      if (nested) return nested
    }
    return null
  }
  return walk(page.sections)
}
